<template>
  <div class="flex items-center gap-2 flex-wrap">
    <!-- Active filter chips. Click body to re-edit, X to clear. -->
    <template v-if="showChips">
      <div
        v-for="chip in activeChips"
        :key="chip.key"
        class="flex items-center rounded-md border bg-white dark:bg-neutral-900/40 text-neutral-700 dark:text-neutral-300 text-sm divide-x divide-neutral-200 dark:divide-neutral-900"
      >
        <button
          type="button"
          class="flex items-center gap-1.5 px-2 py-1 hover:bg-neutral-200/60 dark:hover:bg-neutral-900/60 rounded-l-md cursor-pointer"
          @click="openPicker(chip.key)"
        >
          <UIcon
            v-if="chip.icon"
            :name="chip.icon"
            class="size-3.5 text-neutral-600 dark:text-neutral-400"
          />
          <span class="font-medium opacity-70">{{ chip.label }}</span>
          <span class="opacity-90 truncate max-w-[14rem]">{{ chip.value }}</span>
        </button>
        <button
          type="button"
          class="px-1.5 hover:bg-neutral-200/60 dark:hover:bg-neutral-900/60 rounded-r-md cursor-pointer pt-1.5"
          :aria-label="$t('general.clear')"
          @click="controller.clearFilter(chip.key)"
        >
          <UIcon name="i-lucide-x" class="size-3.5" />
        </button>
      </div>
    </template>

    <!-- Trigger + outer popover (the filter type menu). The trigger style
         depends on `mode` — labelled "Filters" button in toolbars, small
         "+" icon at the end of the chip rail. -->
    <UPopover
      v-if="showTrigger"
      v-model:open="open"
      :content="{ side: 'bottom', align: 'start', sideOffset: 4 }"
      :ui="{ content: 'w-72' }"
      
    >
      <UButton
        v-if="mode === 'chips'"
        icon="i-lucide-plus"
        color="neutral"
        variant="ghost"
        size="xs"
        :aria-label="$t('components.elements.filters.addFilter')"
        @click="openMenu"
      />
      <UButton
        v-else
        icon="i-lucide-list-filter"
        color="neutral"
        variant="subtle"
        size="sm"
        class="rounded-full"
        @click="openMenu"
      />

      <template #content>
        <div class="flex flex-col">
          <div class="px-2 pt-2 pb-1 border-b dark:border-gray-800">
            <UInput
              v-model="menuSearch"
              :placeholder="$t('components.elements.filters.addFilter')"
              icon="i-lucide-search"
              variant="none"
              size="sm"
              autofocus
            />
          </div>

          <div class="max-h-96 overflow-y-auto p-1">
            <template v-for="section in filteredMenuSections" :key="section.label ?? '__'">
              <div
                v-if="section.label"
                class="px-2 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
              >
                {{ section.label }}
              </div>

              <UPopover
                v-for="row in section.rows"
                :key="row.key"
                :open="row.key === activeKey"
                :content="{ side: 'right', align: 'start', sideOffset: 8 }"
                :ui="{ content: 'w-80 max-h-[80vh] overflow-y-auto' }"
                @update:open="(o: boolean) => onNestedOpen(row.key, o)"
              >
                <button
                  type="button"
                  class="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left cursor-pointer"
                  :class="row.key === activeKey
                    ? 'bg-gray-100 dark:bg-gray-800'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'"
                >
                  <UIcon
                    :name="row.icon ?? defaultIconForKind(row.kind)"
                    class="size-4 text-gray-500 dark:text-gray-400"
                  />
                  <span class="text-sm flex-grow truncate">{{ row.label }}</span>
                  <UIcon
                    name="i-lucide-chevron-right"
                    class="size-3.5 text-gray-400"
                  />
                </button>

                <template v-if="row.key === activeKey && activeState" #content>
                  <ListPicker
                    v-if="activeKind === 'list'"
                    :options="activeState.options ?? []"
                    :model-value="activeState.value ?? []"
                    :option-count="activeState.optionCount"
                    @update:model-value="(v) => controller.setValue(activeKey!, v)"
                  />

                  <div v-else-if="activeKind === 'boolean'">
                    <USwitch
                      :model-value="!!activeState.value"
                      :label="activeState.label"
                      @update:model-value="(v) => controller.setValue(activeKey!, v)"
                    />
                  </div>

                  <div v-else-if="activeKind === 'text' || activeKind === 'number'">
                    <UInput
                      size="sm"
                      v-bind="activeState.props"
                      :model-value="activeState.value ?? ''"
                      :type="activeKind === 'number' ? 'number' : 'text'"
                      :placeholder="activeState.label"
                      class="w-full"
                      autofocus
                      @update:model-value="(v) => controller.setValue(activeKey!, v)"
                    />
                  </div>

                  <!-- Custom / date / unhandled: defer to the legacy
                       component registry (CheckboxGroupField, etc.). -->
                  <div v-else >
                    <component
                      :is="resolveCustomComponent(activeState.component)"
                      v-if="activeState.component"
                      :model-value="activeState.value"
                      v-bind="activeState.props"
                      :disabled="activeState.disabled"
                      :options="activeState.options"
                      :items="activeState.options"
                      :custom="activeState.custom"
                      :name="activeState.label"
                      :status="controller.getValidation(activeKey!)?.status ?? null"
                      @update:model-value="(v: any) => controller.setValue(activeKey!, v)"
                    />
                    <div v-else class="text-sm text-gray-500 dark:text-gray-400">
                      {{ $t('components.elements.filters.noPicker') }}
                    </div>
                  </div>

                  <div
                    v-if="!controller.isFilterEmpty(activeKey!)"
                    class="px-2 py-1 border-t dark:border-gray-800 flex justify-end"
                  >
                    <UButton
                      :label="$t('general.clear')"
                      color="neutral"
                      variant="ghost"
                      size="xs"
                      @click="controller.clearFilter(activeKey!)"
                    />
                  </div>
                </template>
              </UPopover>
            </template>

            <div
              v-if="!filteredMenuSections.length"
              class="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
            >
              {{ $t('general.noResults') }}
            </div>
          </div>
        </div>
      </template>
    </UPopover>
  </div>
</template>

<script setup lang="ts" generic="T extends BaseModel">
import type { BaseModel } from '@/_api/models/base'
import type { FilterControllerReturn } from '@/composables/useFilterController'
import type { FilterKind } from '@/_api/types/interfaces/filter'

import ListPicker from './ListPicker.vue'
import { inferFilterKind, matchesSearch } from './utils'

// Custom-component registry — used when a filter sets `kind: 'custom'`
// (explicitly or by inference because it has a `component` field). Kept
// narrow on purpose; widen as new filter UIs are added.
import { CheckboxField, CheckboxGroupField, DateRangeOptionalField, RadioGroupField, SegmentField, SelectField, TextField, ToggleField } from '@/components/fields'
import DateRangeFilter from '@/components/filters/elements/DateRange.vue'
import { OCustomField } from '@/components/elements'

// Built-in registry — generic filter widgets every consuming page can use.
// Page-specific components (e.g. customer's `DateOfBirth`, `FieldsWith
// DateRange`) are merged in via the `filter-components` prop below so the
// registry can stay narrow and `app/components/customer/...` doesn't bleed
// into the generic facet UI.
const BUILT_IN_COMPONENTS: Record<string, any> = {
  CheckboxField,
  CheckboxGroupField,
  DateRangeOptionalField,
  DateRangeFilter,
  OCustomField,
  RadioGroupField,
  SegmentField,
  SelectField,
  TextField,
  ToggleField,
}

interface Props {
  controller: FilterControllerReturn<T>
  /**
   * How the component renders:
   *  - `inline` (default): chips + a labelled "Filters" trigger button next
   *    to them — the all-in-one render.
   *  - `button`: only the labelled trigger. Use in toolbars where the
   *    chip rail is rendered separately.
   *  - `chips`: only the chip rail + a small "+" icon trigger at the end of
   *    it. Use in a dedicated second row.
   */
  mode?: 'inline' | 'button' | 'chips'
  /**
   * Page-specific filter widgets — merged with the built-in registry so
   * filters declared with `component: 'MyCustomThing'` resolve correctly.
   * Pass `{ DateOfBirth, FieldsWithDateRange, ... }` etc. Without this,
   * page-only components would fall through to the "no picker" message.
   */
  filterComponents?: Record<string, any>
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'inline',
  filterComponents: () => ({}),
})

const resolveCustomComponent = (name?: string) => {
  if (!name) return null
  return props.filterComponents[name] ?? BUILT_IN_COMPONENTS[name] ?? null
}

const showChips = computed(() => props.mode === 'inline' || props.mode === 'chips')
const showTrigger = computed(() => props.mode === 'inline' || props.mode === 'button' || props.mode === 'chips')

// ---- Popover state ---------------------------------------------------------
//
// Outer popover = filter type menu. Each menu row is itself a UPopover whose
// `open` is bound to `row.key === activeKey`, so only the active row's picker
// renders. Reka's nested DismissableLayer handles outside-click correctly:
// clicking the picker keeps the menu open; clicking truly outside closes
// everything.

const open = ref(false)
const activeKey = ref<string | null>(null)
const menuSearch = ref('')

watch(open, (isOpen) => {
  if (!isOpen) {
    activeKey.value = null
    menuSearch.value = ''
  }
})

const openMenu = () => {
  activeKey.value = null
  open.value = true
}

/** Programmatic open for chip clicks — opens the menu and pre-selects the row. */
const openPicker = (key: string) => {
  open.value = true
  activeKey.value = key
}

/**
 * Reka calls this when a row's nested popover transitions open/close. Click on
 * the row's trigger button opens it (Reka emits true); outside-click on the
 * picker emits false. We just sync `activeKey` — when one row's picker opens
 * the previously-active row's `:open` flips false automatically through the
 * computed binding.
 */
const onNestedOpen = (key: string, isOpen: boolean) => {
  if (isOpen) {
    activeKey.value = key
  } else if (activeKey.value === key) {
    activeKey.value = null
  }
}

// ---- Active filter chips ---------------------------------------------------

interface FilterChip {
  key: string
  label: string
  value: string
  icon?: string
}

const activeChips = computed<FilterChip[]>(() => {
  const chips: FilterChip[] = []
  const states = props.controller.filterStates.value
  for (const [key, state] of Object.entries(states)) {
    if (!state) continue
    if (inferFilterKind(state) === 'search') continue
    if (props.controller.isFilterEmpty(key)) continue
    const label = props.controller.getValueLabel(key)
    if (!label) continue
    chips.push({
      key,
      label: state.label ?? key,
      value: label,
      icon: state.icon,
    })
  }
  return chips
})

// ---- Menu sections (grouped by FilterCategoryConfig if categories defined) -

interface MenuRow {
  key: string
  label: string
  icon?: string
  kind: FilterKind
}
interface MenuSection {
  label: string | null
  rows: MenuRow[]
}

const allRows = computed<MenuRow[]>(() => {
  const states = props.controller.filterStates.value
  const rows: MenuRow[] = []
  for (const [key, state] of Object.entries(states)) {
    if (!state) continue
    const kind = inferFilterKind(state)
    if (kind === 'search') continue
    rows.push({
      key,
      label: state.label ?? key,
      icon: state.icon,
      kind,
    })
  }
  return rows
})

const menuSections = computed<MenuSection[]>(() => {
  const cats = props.controller.categories.value
  if (!cats?.length) {
    return [{ label: null, rows: allRows.value }]
  }
  // Build categorised sections, skipping empty ones. Filters not in any
  // category fall into a trailing "Other" section so they're still reachable.
  const claimedKeys = new Set<string>()
  const sections: MenuSection[] = []
  for (const cat of cats) {
    const rows: MenuRow[] = []
    for (const fk of cat.filters) {
      const state = props.controller.filterStates.value[fk]
      if (!state) continue
      const kind = inferFilterKind(state)
      if (kind === 'search') continue
      claimedKeys.add(fk)
      rows.push({ key: fk, label: state.label ?? fk, icon: state.icon, kind })
    }
    if (rows.length) sections.push({ label: cat.label, rows })
  }
  const orphanRows = allRows.value.filter((r) => !claimedKeys.has(r.key))
  if (orphanRows.length) {
    sections.push({ label: sections.length ? null : null, rows: orphanRows })
  }
  return sections
})

const filteredMenuSections = computed<MenuSection[]>(() => {
  if (!menuSearch.value) return menuSections.value
  return menuSections.value
    .map((s) => ({
      label: s.label,
      rows: s.rows.filter((r) => matchesSearch(r.label, menuSearch.value)),
    }))
    .filter((s) => s.rows.length > 0)
})

// ---- Active picker state ---------------------------------------------------

const activeState = computed(() => {
  return activeKey.value
    ? props.controller.filterStates.value[activeKey.value]
    : null
})
const activeKind = computed<FilterKind>(() => inferFilterKind(activeState.value))

// ---- Icon defaults ---------------------------------------------------------

const defaultIconForKind = (kind: FilterKind): string => {
  switch (kind) {
    case 'list': return 'i-lucide-list'
    case 'date': return 'i-lucide-calendar'
    case 'boolean': return 'i-lucide-toggle-right'
    case 'text': return 'i-lucide-type'
    case 'number': return 'i-lucide-hash'
    default: return 'i-lucide-filter'
  }
}
</script>
